import React from 'react';

import Icon from '@/components/ui/AppIcon';
import Image from 'next/image';
import Link from 'next/link';

interface ProductEcosystemProps {
  className?: string;
}

const ProductEcosystem: React.FC<ProductEcosystemProps> = ({ className = '' }) => {
  const capabilities = [
  {
    id: 'knowledge',
    title: 'Knowledge becomes a growth asset',
    description: 'Sales and support teams learn continuously. What works gets amplified. What doesn\'t gets fixed automatically, so every interaction improves the next one.',
    icon: 'LightBulbIcon',
    useImage: true,
    imagePath: '/assets/images/Annotation_2026-01-30_162343-1769770433490.png',
    color: 'from-accent to-brand-teal'
  },
  {
    id: 'engagement',
    title: 'Engagement becomes intelligent',
    description: 'Human teams and AI agents work together across assisted, digital, video, and physical channels to convert, retain, and grow customers.',
    icon: 'UserGroupIcon',
    useImage: true,
    imagePath: '/assets/images/Annotation_2026-01-30_165210-1769772150724.png',
    color: 'from-primary to-accent'
  },
  {
    id: 'performance',
    title: 'Performance protects profitability',
    description: 'Productivity, security, and compliance are built in, ensuring growth scales without leakage or risk.',
    icon: 'ShieldCheckIcon',
    useImage: true,
    imagePath: '/assets/images/Layer_x0020_1-1770726496170.png',
    color: 'from-trust to-success'
  },
  {
    id: 'physical',
    title: 'Physical data points fuel digital decisions',
    description: 'Real-world interactions and environments feed intelligence back into the system, closing the loop between experience and outcome.',
    icon: 'ArrowPathIcon',
    useImage: true,
    imagePath: "/assets/images/Devicemax-logo_1-1770726659660.png",
    color: 'from-conversion to-warning'
  }];


  return (
    <section id="unifymax-techstack" className={`py-20 bg-surface ${className}`}>
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-12">
            <h2 className="font-display font-bold text-3xl lg:text-5xl text-foreground mb-4">
              UnifyMax Techstack
            </h2>
            <p className="font-body text-lg text-muted-foreground max-w-3xl mx-auto">
              Four integrated capabilities that transform customer engagement into business growth
            </p>
          </div>

          {/* How UnifyMax Works */}
          <div className="grid md:grid-cols-2 gap-8">
            {capabilities.map((capability) => {
              const CardContent =
              <div
                className="bg-card rounded-2xl p-8 shadow-md hover:shadow-xl transition-all duration-300 border border-border h-full">
                  {capability.useImage ?
                <div className="mb-6">
                      <Image
                    src={capability.imagePath!}
                    alt={capability.title}
                    width={150}
                    height={110}
                    className="object-contain" />
                  
                    </div> :

                <div className={`bg-gradient-to-br ${capability.color} rounded-xl p-4 inline-block mb-6`}>
                      <Icon name={capability.icon as any} size={32} variant="solid" className="text-white" />
                    </div>
                }
                  <h3 className="font-display font-bold text-xl lg:text-2xl text-foreground mb-4">
                    {capability.title}
                  </h3>
                  <p className="font-body text-base text-muted-foreground leading-relaxed">
                    {capability.description}
                  </p>
                </div>;


              // Wrap PhygitalMax card with link
              if (capability.id === 'engagement') {
                return (
                  <Link
                    key={capability.id}
                    href="https://phygitalmax.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block group">
                    
                    {CardContent}
                  </Link>);

              }

              // Wrap KnowMax card with link
              if (capability.id === 'knowledge') {
                return (
                  <Link
                    key={capability.id}
                    href="https://knowmax.ai/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block group">
                    
                    {CardContent}
                  </Link>);

              }

              // Wrap LeapMax card with link
              if (capability.id === 'performance') {
                return (
                  <Link
                    key={capability.id}
                    href="https://leapmax.ai/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block group">
                    
                    {CardContent}
                  </Link>);

              }

              // Wrap DeviceMax card with link
              if (capability.id === 'physical') {
                return (
                  <Link
                    key={capability.id}
                    href="https://devicemax.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block group">
                    
                    {CardContent}
                  </Link>);

              }

              return (
                <div key={capability.id}>
                  {CardContent}
                </div>);

            })}
          </div>
        </div>
      </div>
    </section>);

};

export default ProductEcosystem;